import React, { useMemo, useState } from 'react';
import ImageUploadBox from '@/components/common/ImageUploadBox';
import { Select } from '@/components/common/Select';
import { useDict } from '@/hooks/useDict';

export interface BasicInfoFormData extends Api.Application.Step1Request {
  contract_id?: string;
  cover_asset_id?: string;
}

interface BasicInfoFormProps {
  data: BasicInfoFormData;
  onChange: (data: BasicInfoFormData) => void;
}

const PRESET_GENRES = [
  { label: '甜宠', code: 'SWEET_LOVE' },
  { label: '霸总', code: 'CEO' },
  { label: '战神', code: 'WARRIOR' },
  { label: '都市', code: 'URBAN' },
  { label: '悬疑', code: 'SUSPENSE' },
  { label: '奇幻', code: 'FANTASY' },
  { label: '古装', code: 'COSTUME' },
  { label: '复仇', code: 'REVENGE' },
  { label: '修仙', code: 'XIANXIA' },
  { label: '科幻', code: 'SCI_FI' },
];

export default function BasicInfoForm({
  data,
  onChange,
}: BasicInfoFormProps) {
  // 从字典中获取各种字典配置数据
  const { items: genreDictItems, loading: loadingGenres } = useDict('GENRE');
  const { options: dictOrientationOptions, loading: loadingOrientation } = useDict('CONTENT_ORIENTATION');
  const { options: dictContentTypeOptions, loading: loadingContentType } = useDict('CONTENT_TYPE');
  const { options: dictLanguageOptions, loading: loadingLanguage } = useDict('LANGUAGE');
  const { options: dictAudienceOptions, loading: loadingAudience } = useDict('AUDIENCE');

  const genres = useMemo(() => {
    if (genreDictItems && genreDictItems.length > 0) {
      return genreDictItems.map((item) => ({
        label: item.item_name,
        code: item.item_code,
      }));
    }
    return PRESET_GENRES;
  }, [genreDictItems]);

  const orientationOptions = useMemo(() => {
    if (dictOrientationOptions && dictOrientationOptions.length > 0) {
      return dictOrientationOptions;
    }
    return [
      { label: '竖屏', value: 'VERTICAL' },
      { label: '横屏', value: 'HORIZONTAL' },
    ];
  }, [dictOrientationOptions]);

  const contentTypeOptions = useMemo(() => {
    if (dictContentTypeOptions && dictContentTypeOptions.length > 0) {
      return dictContentTypeOptions;
    }
    return [
      { label: '短剧', value: 'SHORT_DRAMA' },
      { label: '微电影', value: 'MICRO_MOVIE' },
      { label: '动漫', value: 'ANIMATION' },
      { label: '其他', value: 'OTHER' },
    ];
  }, [dictContentTypeOptions]);

  const languageOptions = useMemo(() => {
    if (dictLanguageOptions && dictLanguageOptions.length > 0) {
      return dictLanguageOptions;
    }
    return [
      { label: '中文简体', value: 'zh-CN' },
      { label: '英语', value: 'en-US' },
      { label: '日语', value: 'ja-JP' },
      { label: '韩语', value: 'ko-KR' },
    ];
  }, [dictLanguageOptions]);

  const audienceOptions = useMemo(() => {
    if (dictAudienceOptions && dictAudienceOptions.length > 0) {
      return dictAudienceOptions;
    }
    return [
      { label: '全人群/通用', value: 'GENERAL' },
      { label: '青少年', value: 'TEEN' },
      { label: '成人群体', value: 'ADULT' },
      { label: '少儿/儿童', value: 'KIDS' },
    ];
  }, [dictAudienceOptions]);

  const update = <K extends keyof BasicInfoFormData>(field: K, value: BasicInfoFormData[K]) => {
    onChange({ ...data, [field]: value });
  };

  const removeGenre = (code: string) => {
    const current = data.genre_codes || [];
    update('genre_codes', current.filter((c) => c !== code));
  };

  return (
    <div className="space-y-6">
      {/* Info Tip */}
      <div className="flex items-start gap-2.5 p-4 rounded-lg bg-gray-50 text-sm text-foreground-600 border border-gray-100">
        <i className="ri-question-line text-foreground-400 text-base mt-0.5 flex-shrink-0"></i>
        <span>请填写完整的剧集基本信息，带 <span className="text-red-500 font-bold">*</span> 的为必填项。</span>
      </div>


      {/* 作品名 */}
      <div>
        <label className="block text-sm font-bold text-foreground-800 mb-1">
          作品名 <span className="text-red-500">*</span>
        </label>
        <p className="text-xs md:text-sm text-foreground-400 mb-2">标题建议不超过 35 个字符</p>
        <input
          type="text"
          value={data.title || ''}
          onChange={(e) => update('title', e.target.value)}
          placeholder="输入作品名称"
          className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200/80 text-base text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
        />
      </div>

      {/* 副标题 */}
      <div>
        <label className="block text-sm font-bold text-foreground-800 mb-2">
          副标题
        </label>
        <input
          type="text"
          value={data.subtitle || ''}
          onChange={(e) => update('subtitle', e.target.value)}
          placeholder="输入副标题（选填）"
          className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200/80 text-base text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
        />
      </div>

      {/* 内容类型 & 语种 & 朝向 & 受众人群 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-bold text-foreground-800 mb-2">
            内容类型 <span className="text-red-500">*</span>
          </label>
          <Select
            value={data.content_type_code || 'SHORT_DRAMA'}
            onChange={(val) => update('content_type_code', String(val))}
            options={contentTypeOptions}
            loading={loadingContentType}
            loadingText="加载中..."
            placeholder="请选择内容类型"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-foreground-800 mb-2">
            源语种 <span className="text-red-500">*</span>
          </label>
          <Select
            value={data.source_language_code || 'zh-CN'}
            onChange={(val) => update('source_language_code', String(val))}
            options={languageOptions}
            loading={loadingLanguage}
            loadingText="加载中..."
            placeholder="请选择源语种"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-foreground-800 mb-2">
            画面朝向 <span className="text-red-500">*</span>
          </label>
          <Select
            value={data.orientation || 'VERTICAL'}
            onChange={(val) => update('orientation', String(val))}
            options={orientationOptions}
            loading={loadingOrientation}
            loadingText="加载中..."
            placeholder="请选择画面朝向"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-foreground-800 mb-2">
            受众人群 <span className="text-red-500">*</span>
          </label>
          <Select
            value={data.audience_code || 'GENERAL'}
            onChange={(val) => update('audience_code', String(val))}
            options={audienceOptions}
            loading={loadingAudience}
            loadingText="加载中..."
            placeholder="请选择受众人群"
          />
        </div>
      </div>

      {/* 题材分类标签 */}
      <div>
        <label className="block text-sm font-bold text-foreground-800 mb-2">
          题材分类 <span className="text-red-500">*</span>
        </label>

        {/* 已选标签显示列表 */}
        {(data.genre_codes || []).length > 0 && (
          <div className="flex flex-wrap items-center gap-2.5 mb-3 p-3.5 rounded-lg bg-gray-50 border border-gray-200/60">
            <span className="text-sm text-foreground-500 font-medium mr-1">已选择：</span>
            {(data.genre_codes || []).map((code) => {
              const match = genres.find((p) => p.code === code);
              const displayName = match ? match.label : code;
              return (
                <span
                  key={code}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium bg-primary-500 text-foreground-950 shadow-2xs"
                >
                  {displayName}
                  <button
                    type="button"
                    onClick={() => removeGenre(code)}
                    className="hover:text-red-700 cursor-pointer transition-colors ml-0.5"
                    title="删除标签"
                  >
                    <i className="ri-close-line text-sm font-bold"></i>
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* 常用题材标签 */}
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-sm text-foreground-500 mr-1">常用推荐：</span>
          {loadingGenres ? (
            <span className="text-sm text-foreground-400">加载题材字典中...</span>
          ) : (
            genres.map((g) => {
              const selected = (data.genre_codes || []).includes(g.code);
              return (
                <button
                  key={g.code}
                  type="button"
                  onClick={() => {
                    if (selected) {
                      removeGenre(g.code);
                    } else {
                      update('genre_codes', [...(data.genre_codes || []), g.code]);
                    }
                  }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer border ${selected
                      ? 'bg-primary-100 text-primary-800 border-primary-300 font-semibold'
                      : 'bg-white text-foreground-700 border-gray-200/80 hover:border-gray-300'
                    }`}
                >
                  {selected ? <i className="ri-check-line mr-1 text-primary-600"></i> : <i className="ri-add-line mr-1 text-foreground-400"></i>}
                  {g.label}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* 真人实拍与 AI 内容声明 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center gap-3.5 p-4 rounded-lg border border-gray-200/80 bg-gray-50/50 cursor-pointer hover:bg-gray-50 transition-all">
          <input
            type="checkbox"
            checked={data.has_live_actor ?? true}
            onChange={(e) => update('has_live_actor', e.target.checked)}
            className="w-4.5 h-4.5 text-primary-500 rounded border-gray-300 focus:ring-primary-400 cursor-pointer"
          />
          <div>
            <span className="text-sm font-bold text-foreground-800">真人实拍内容</span>
            <p className="text-xs md:text-sm text-foreground-400 mt-0.5">包含真人演员出镜拍摄的影像</p>
          </div>
        </label>

        <label className="flex items-center gap-3.5 p-4 rounded-lg border border-gray-200/80 bg-gray-50/50 cursor-pointer hover:bg-gray-50 transition-all">
          <input
            type="checkbox"
            checked={data.is_ai_content ?? false}
            onChange={(e) => update('is_ai_content', e.target.checked)}
            className="w-4.5 h-4.5 text-primary-500 rounded border-gray-300 focus:ring-primary-400 cursor-pointer"
          />
          <div>
            <span className="text-sm font-bold text-foreground-800">AI 辅助/生成内容</span>
            <p className="text-xs md:text-sm text-foreground-400 mt-0.5">包含 AIGC 绘画、AI 换脸或合成音视频</p>
          </div>
        </label>
      </div>

      {/* 剧集描述 */}
      <div>
        <label className="block text-sm font-bold text-foreground-800 mb-2">
          剧集描述 <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.description || ''}
          onChange={(e) => update('description', e.target.value)}
          placeholder="概述你的内容，添加精彩细节吸引更多观众"
          rows={4}
          maxLength={1500}
          className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200/80 text-base text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all resize-none"
        />
        <p className="text-sm text-foreground-400 mt-1 text-right">{(data.description || '').length}/1500</p>
      </div>
    </div>
  );
}
